import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "hooks/useApi";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "./useNotification";

type Props = {
  entityId: string;
  data?: any;
};

export const useFetchEntity = ({ entityId }: Props) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["entity", sport, organizationId, entityId],
    queryFn: async () => {
      const response = await callApi(`/${sport}/o/${organizationId}/entities/${entityId}`);
      const entity = response?.data?.[0];
      if (response?.includes?.resources && entity?.entityGroup) {
        entity.entityGroup = {
          ...entity.entityGroup,
          ...response.includes.resources[entity.entityGroup.resourceType]?.[entity.entityGroup.id],
        };
      }
      return response;
    },
    enabled: !!entityId,
  });
};

export const useFetchEntities = () => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["entities", sport, organizationId],
    queryFn: () => callApi(`/${sport}/o/${organizationId}/entities`),
  });
};

export const useUpdateEntity = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, data }: Props) => {
      return callApi(`/${sport}/o/${organizationId}/entities/${entityId}`, {
        method: "PUT",
        body: data,
      });
    },
    onSuccess: (_, { entityId }: Props) => {
      queryClient.invalidateQueries({
        queryKey: ["entity", sport, organizationId, entityId],
      });
      queryClient.invalidateQueries({
        queryKey: ["entities", sport, organizationId],
      });
      showSuccessNotification(formatMessage({ id: "notification.entity.updated" }));
    },
  });
};
